import React, { ChangeEvent, Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Button, TextField, Typography } from '@mui/material';
import { NavigateFunction } from 'react-router-dom';
import moment from 'moment';

import { showNotification, showErrorNotification } from '../../redux/actions';
import ApiClient from '../../loopback/apiClient';
import LoopbackProvider from '../../loopback/LoopbackProvider';
import GenericDialog from '../GenericDialog';
import ContentBox from '../layout/ContentBox';
import { getInvoiceDownloadUrl } from '../../environment';
import Payment from './Payment';
import { downloadFromContainer } from '../../utilities/urlHelper';
import {
  Enrollment,
  Invoice,
  RootState,
  Submission,
  Trial,
  Payment as PaymentType,
} from '../../redux/types';

type State = {
  orderNumber: string;
  confirmNoOrderNumber: boolean;
  submissions?: {
    values: Submission[];
    reload: () => void;
  }
  enrollments?: (Enrollment & { invoices: (Invoice & { payment: PaymentType })[] });
}

class TrialInvoiceDialog extends Component<Props, State> {
  static isLegacyEnrollment = (enrollment: Enrollment): boolean => moment.utc(enrollment.createdAt).isBefore(moment.utc('2018-05-02'));

  static isPayoneEnrollment = (
    currentInvoice?: Invoice & { payment: PaymentType },
  ): boolean | string | undefined => currentInvoice
  && currentInvoice.payment.bookedAt && !currentInvoice.payment.cancelledAt;

  constructor(props: Props) {
    super(props);
    this.state = {
      orderNumber: '',
      confirmNoOrderNumber: false,
    };
  }

  componentDidMount() {
    this.fetchEnrollmentWithInvoices();
  }

  componentDidUpdate(prevProps: Props, prevState: State): void {
    const { enrollment } = this.props;
    const { enrollment: prevEnrollment } = prevProps;
    const { enrollments } = this.state;
    if (enrollment !== prevEnrollment) {
      this.fetchEnrollmentWithInvoices();
    }
    if (prevState.enrollments && prevState.enrollments !== enrollments) {
      const invoices = enrollments?.invoices;
      const currentInvoice = invoices && invoices.filter((invoice) => !invoice.cancelledAt)[0];
      if (currentInvoice && typeof currentInvoice !== 'boolean') {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ orderNumber: TrialInvoiceDialog.getOrderNumber(currentInvoice) });
      }
    }
  }

  getDownloadButton = (invoice?: Invoice): React.ReactNode => {
    const { showErrorNotification: showErrorNotificationAction } = this.props;
    return (
      <Button
        variant="contained"
        className="ml2"
        id="InvoiceDialog-downloadInvoiceButton"
        onClick={() => {
          if (!invoice) {
            return;
          }
          ApiClient.getInvoicePdf(invoice.id)
            .then((fileName) => {
              downloadFromContainer('invoices', fileName);
            })
            .catch((e) => {
              if (e.message === 'NO_TRANSACTION_ID') {
                showErrorNotificationAction(<FormattedMessage id="payment.inProcess" />);
              } else {
                showErrorNotificationAction(e.message);
              }
            });
        }}
        color="primary"
        disabled={!invoice || !!invoice.cancelledAt || !invoice.fileName}
      >
        <FormattedMessage id="enrollmentsmanager.downloadInvoiceButton" />
      </Button>
    );
  };

  getEInvoiceButton = (invoice?: Invoice): React.ReactNode => {
    const { showErrorNotification: showErrorNotificationAction } = this.props;
    return (
      <Button
        variant="contained"
        className="ml2"
        id="InvoiceDialog-downloadEInvoiceButton"
        onClick={() => {
          if (!invoice) {
            return;
          }
          ApiClient.getEInvoice(invoice.id)
            .then((fileName) => {
              downloadFromContainer('invoices', fileName);
            })
            .catch((e) => {
              showErrorNotificationAction(e.message);
            });
        }}
        color="primary"
        disabled={!invoice || !!invoice.cancelledAt || !invoice.fileName}
      >
        <FormattedMessage id="enrollmentsmanager.downloadEInvoiceButton" />
      </Button>
    );
  };

  static getOrderNumber = (invoice?: Invoice): string => {
    if (invoice && !invoice.cancelledAt && invoice.orderNumber) {
      return invoice.orderNumber;
    }
    return '';
  };

  getWarningText(): JSX.Element | boolean {
    const { confirmNoOrderNumber } = this.state;
    if (confirmNoOrderNumber) {
      return <FormattedMessage id="enrollmentsmanager.orderNumberWarningText" />;
    }

    return confirmNoOrderNumber;
  }

  getCreateInvoiceText(): JSX.Element | boolean {
    const { confirmNoOrderNumber } = this.state;
    if (confirmNoOrderNumber) {
      return <FormattedMessage id="confirm" />;
    }

    return <FormattedMessage id="enrollmentsmanager.setInvoiceNumber" />;
  }

  fetchEnrollmentWithInvoices = async () => {
    const { enrollment } = this.props;
    if (!enrollment) {
      return;
    }
    const enrollments = await ApiClient.getEnrollmentWithInvoices(enrollment?.id);
    this.setState({ enrollments });
  };

  updateOrderNumber = (event: ChangeEvent<HTMLInputElement>): void => {
    this.setState({
      orderNumber: event.target.value,
      confirmNoOrderNumber: false,
    });
  };

  setInvoiceOrderNumber = (currentInvoice: Invoice, orderNumber: string): void => {
    const { showNotification: showNotificationAction } = this.props;
    const { orderNumber: stateOrderNummber, confirmNoOrderNumber } = this.state;
    if (stateOrderNummber !== '' || confirmNoOrderNumber) {
      ApiClient.setInvoiceOrderNumber(currentInvoice.id, orderNumber)
        .then(this.fetchEnrollmentWithInvoices)
        .then(() => showNotificationAction(<FormattedMessage id="enrollmentsmanager.invoiceNumberSet" />));
    } else {
      this.setState({
        confirmNoOrderNumber: true,
      });
    }
  };

  renderInvoiceForm = (
    currentEnrollment: Enrollment, currentInvoice?: Invoice,
  ): JSX.Element => {
    const { guidingText, onClose } = this.props;
    const { confirmNoOrderNumber, orderNumber, enrollments } = this.state;
    return (
      <ContentBox title={<FormattedMessage id="enrollmentsmanager.invoiceDialogTitle" />}>
        {guidingText && (
          <Typography variant="body2" className="mb2">
            {guidingText}
          </Typography>
        )}
        <TextField
          className="mb2"
          style={{ width: '100%' }}
          id="InvoiceDialog-orderNumberTextField"
          error={confirmNoOrderNumber}
          helperText={this.getWarningText()}
          label={<FormattedMessage id="enrollmentsmanager.orderNumberTextFieldLabel" />}
          onChange={this.updateOrderNumber}
          value={orderNumber}
          disabled={
            TrialInvoiceDialog.isLegacyEnrollment(currentEnrollment)
              ? (currentInvoice && !currentInvoice.cancelledAt && !!currentInvoice.fileName)
              : (enrollments?.status !== 'approved')
          }
        />
        <Typography variant="body2" className="mb2">
          <FormattedMessage id="payment.aboutOrderNumber" />
        </Typography>
        <div className="flex justify-between">
          <div style={{ width: '100%' }}>
            <Button
              variant="contained"
              id="InvoiceDialog-createInvoiceButton"
              onClick={() => {
                if (currentInvoice) {
                  this.setInvoiceOrderNumber(currentInvoice, orderNumber);
                }
              }}
              color="primary"
              disabled={
                TrialInvoiceDialog.isLegacyEnrollment(currentEnrollment)
                  ? (currentInvoice && !!currentInvoice.fileName)
                  : (false)
                  || currentEnrollment.status === 'cancelled'
              }
            >
              {this.getCreateInvoiceText()}
            </Button>
            {this.getDownloadButton(currentInvoice)}
            {this.getEInvoiceButton(currentInvoice)}
            <Button style={{ float: 'right' }} variant="contained" onClick={() => onClose()} color="secondary">
              <FormattedMessage id="genericClose" />
            </Button>
          </div>
        </div>
      </ContentBox>
    );
  };

  static renderCancelledInvoices = (cancelledInvoices: Invoice[]): JSX.Element => (
    <div className="mt4">
      <h1>
        <FormattedMessage id="enrollmentsmanager.cancelledInvoices" />
      </h1>
      <ul className="list pa0">
        {cancelledInvoices && cancelledInvoices.map((cancelledInvoice) => (
          <li key={cancelledInvoice.invoiceNumber}>
            <a href={getInvoiceDownloadUrl(cancelledInvoice.fileName, LoopbackProvider.loopbackToken.id)} target="_blank" rel="noopener noreferrer">
              {cancelledInvoice.invoiceNumber}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );

  renderPaymentForm = (currentInvoice?: Invoice & { payment: PaymentType }): JSX.Element => {
    const { trial, onClose, navigate } = this.props;
    const { enrollments } = this.state;
    return (
      !currentInvoice || !currentInvoice.payment.bookedAt
        ? (
          <Payment
            enrollment={enrollments}
            trial={trial}
            onCancel={() => onClose()}
            navigate={navigate}
          />
        )
        : (
          <div>
            <div className="mb3">
              <FormattedMessage id="payment.pending" />
            </div>
            <Button variant="contained" onClick={() => onClose()} color="secondary">
              <FormattedMessage id="genericClose" />
            </Button>
          </div>
        )
    );
  };

  renderInvoiceOrPaymentForm = (
    currentEnrollment: Enrollment,
    currentInvoice?: Invoice & { payment: PaymentType },
  ): JSX.Element => ((TrialInvoiceDialog.isLegacyEnrollment(currentEnrollment)
    || TrialInvoiceDialog.isPayoneEnrollment(currentInvoice))
    ? this.renderInvoiceForm(currentEnrollment, currentInvoice)
    : this.renderPaymentForm(currentInvoice));

  renderTrialCancellationNotice = (): JSX.Element => {
    const { onClose } = this.props;
    return (
      <ContentBox title={<FormattedMessage id="enrollmentsmanager.trialCancelledHeading" />}>
        <div>
          <div className="mb2">
            <FormattedMessage id="enrollmentsmanager.trialCancellationNote" />
          </div>
          <Button variant="contained" onClick={() => onClose()} color="secondary">
            <FormattedMessage id="genericClose" />
          </Button>
        </div>
      </ContentBox>
    );
  };

  render(): React.ReactNode {
    const { isOpen, enrollment } = this.props;
    const { enrollments } = this.state;
    if (!enrollment) {
      return null;
    }
    const invoices = enrollments?.invoices
      ? enrollments.invoices : undefined;
    const currentInvoice = invoices && invoices.filter((invoice) => !invoice.cancelledAt)[0];
    const cancelledInvoices = invoices && invoices.filter((invoice) => invoice.cancelledAt);
    const currentEnrollment = enrollments;
    return (
      <>
        {currentEnrollment
          && (
            <GenericDialog isOpen={isOpen} maxWidth="lg">
              {currentEnrollment.status === 'cancelled'
                ? this.renderTrialCancellationNotice()
                : this.renderInvoiceOrPaymentForm(
                  currentEnrollment, currentInvoice,
                )}
              {
                cancelledInvoices
                && !!cancelledInvoices.length
                && TrialInvoiceDialog.renderCancelledInvoices(cancelledInvoices)
              }
            </GenericDialog>
          )}
      </>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  locale: state.intl.locale,
});

const dispatchProps = {
  showNotification,
  showErrorNotification,
};

const connector = connect(mapStateToProps, dispatchProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & {
  isOpen: boolean;
  onClose: () => void;
  trial: Trial;
  enrollment?: Enrollment;
  guidingText?: React.ReactNode;
  navigate: NavigateFunction;
}
export default connector(TrialInvoiceDialog);
